import { useEffect, useState } from 'react';
import { usersApiService } from '../services/usersApi';
import { usernameCacheService } from '../services/usernameCache';
import type { PlayerStats } from '../types/player';

export interface DiscordPlayer {
  uuid: string;
  username: string;
  /** Full Users API record (playtime, last server, ban/booster flags, counts). */
  stats: PlayerStats;
}

// Module-level cache: Discord ID → linked Minecraft account (null = no linked account).
// Negative results are cached too so a 404 is not retried on every render.
const discordPlayerCache = new Map<string, DiscordPlayer | null>();
const pendingLookups = new Map<string, Promise<DiscordPlayer | null>>();
const UPDATED_EVENT = 'discordPlayersUpdated';

function lookup(discordId: string): Promise<DiscordPlayer | null> {
  const cached = discordPlayerCache.get(discordId);
  if (cached !== undefined) return Promise.resolve(cached);

  let pending = pendingLookups.get(discordId);
  if (!pending) {
    pending = usersApiService
      .getPlayerStatsByDiscordId(discordId)
      .then((response) => {
        const stats = response.player_stats;
        if (!stats?.uuid) return null;
        const player: DiscordPlayer = { uuid: stats.uuid, username: stats.username, stats };
        usernameCacheService.seed(player.uuid, player.username);
        return player;
      })
      .catch(() => null)
      .then((player) => {
        discordPlayerCache.set(discordId, player);
        document.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: discordId }));
        return player;
      })
      .finally(() => {
        pendingLookups.delete(discordId);
      });
    pendingLookups.set(discordId, pending);
  }
  return pending;
}

/** Synchronous cache read. `undefined` = not looked up yet, `null` = no linked account. */
export const getCachedDiscordPlayer = (discordId: string): DiscordPlayer | null | undefined =>
  discordPlayerCache.get(discordId);

/**
 * Resolves a Discord ID to the linked Minecraft account (for `PlayerProfile` deep links
 * and player facts). Shares one cache across the roster and profile pages; each ID hits
 * the Users API at most once per session.
 */
export const useDiscordPlayer = (discordId: string | null | undefined) => {
  const initial = discordId ? discordPlayerCache.get(discordId) : null;
  const [player, setPlayer] = useState<DiscordPlayer | null | undefined>(initial);

  useEffect(() => {
    if (!discordId) {
      setPlayer(null);
      return;
    }
    let cancelled = false;
    const cached = discordPlayerCache.get(discordId);
    if (cached !== undefined) {
      setPlayer(cached);
      return;
    }
    setPlayer(undefined);
    lookup(discordId).then((result) => {
      if (!cancelled) setPlayer(result);
    });
    return () => {
      cancelled = true;
    };
  }, [discordId]);

  return {
    uuid: player?.uuid ?? null,
    username: player?.username ?? null,
    stats: player?.stats ?? null,
    loading: player === undefined,
    notFound: player === null,
  };
};

/**
 * Warms the cache for a list of Discord IDs with bounded concurrency. Call once after a
 * roster loads so the table can render avatars without a request storm.
 */
export const prefetchDiscordPlayers = async (discordIds: string[], concurrency = 4): Promise<void> => {
  const queue = [...new Set(discordIds)].filter((id) => id && !discordPlayerCache.has(id));
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length > 0) {
      const id = queue.shift();
      if (id) await lookup(id);
    }
  });
  await Promise.all(workers);
};
